/**
 * SpecialtyIntakeCard
 * ───────────────────────────────────────────────────────
 * Pre-visit overview card that surfaces MA-entered intake
 * values specific to the active specialty (growth metrics
 * for Peds, PHQ-9/GAD-7 for Psych, OB prenatal math, ASA
 * + surgical status). Renders inside the Overview tab,
 * BEFORE the visit begins — mirrors real-world workflow
 * where the MA/RN captures vitals and screeners.
 */
import {
  Baby, Brain, Stethoscope, Scissors, HeartPulse, Ruler, Syringe, Scale,
  Activity, ShieldAlert, ClipboardCheck, FileText, Calendar, AlertTriangle, Camera,
} from 'lucide-react';
import { Badge } from '../../../ui/badge';
import { percentile } from '../../../../config/growthCurves';

/* "Scan with Lens" bridge — MA can pull values straight from a paper form. */
const LensBridgeBtn = ({ onClick, label = 'Scan with Lens', testId }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-700 hover:text-white bg-white hover:bg-slate-900 px-2 py-1 rounded-md border border-slate-300 hover:border-slate-900 transition-colors"
    data-testid={testId}
    title="Open Lumina Lens to scan/upload an intake form"
  >
    <Camera className="h-3 w-3" /> {label}
  </button>
);

const Tile = ({ label, value, accent = 'slate', testId, sub }) => {
  const tone = {
    slate:   'text-slate-800',
    rose:    'text-rose-700',
    amber:   'text-amber-700',
    red:     'text-red-700',
    emerald: 'text-emerald-700',
    indigo:  'text-indigo-700',
    sky:     'text-sky-700',
  }[accent] || 'text-slate-800';
  return (
    <div className="space-y-0.5" data-testid={testId}>
      <p className="text-[9px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`text-[12px] font-semibold tabular-nums ${tone}`}>{value ?? '—'}</p>
      {sub && <p className="text-[9px] text-slate-400">{sub}</p>}
    </div>
  );
};

/* ════════════ PEDIATRICS OVERVIEW ════════════ */
const PedsOverview = ({ intake, onScan }) => {
  const { ageMonths, sex, weight_kg, length_cm, hc_cm, milestones, vaccinesDue } = intake;
  const wPct = weight_kg != null ? percentile('weight', sex, ageMonths, weight_kg) : null;
  const lPct = length_cm != null ? percentile('length', sex, ageMonths, length_cm) : null;
  const hcPct = hc_cm != null ? percentile('hc', sex, ageMonths, hc_cm) : null;
  const heightM = length_cm ? length_cm / 100 : null;
  const bmi = heightM && weight_kg ? weight_kg / (heightM * heightM) : null;
  const bPct = bmi != null ? percentile('bmi', sex, ageMonths, bmi) : null;
  const ageLabel = ageMonths >= 24 ? `${Math.floor(ageMonths / 12)}y` : `${ageMonths} mo`;

  const toneFor = (p) => !p ? 'slate' : (p.percentile >= 97 || p.percentile <= 3) ? 'red' : (p.percentile >= 85 || p.percentile <= 15) ? 'amber' : 'emerald';

  return (
    <div className="rounded-xl border border-sky-200 bg-sky-50/30 p-4 space-y-3" data-testid="specialty-intake-peds">
      <div className="flex items-center gap-2 flex-wrap">
        <Baby className="h-4 w-4 text-sky-700" />
        <p className="text-xs font-bold text-slate-900">Pediatric intake</p>
        <Badge className="text-[9px] bg-sky-100 text-sky-800 border-0">{ageLabel} · {sex === 'male' ? 'M' : 'F'}</Badge>
        <Badge className="text-[9px] bg-white text-slate-500 border border-slate-200">MA-entered</Badge>
        {vaccinesDue?.length > 0 && (
          <Badge className="text-[9px] bg-amber-100 text-amber-800 border-0">{vaccinesDue.length} vaccine{vaccinesDue.length !== 1 ? 's' : ''} due</Badge>
        )}
        <div className="ml-auto">
          {onScan && <LensBridgeBtn onClick={() => onScan('peds_vitals')} label="Scan growth card" testId="peds-intake-scan-btn" />}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 rounded-lg bg-white border border-slate-100">
        <Tile label="Weight" value={weight_kg != null ? `${weight_kg} kg` : '—'} sub={wPct ? `${wPct.percentile}%ile` : null} accent={toneFor(wPct)} testId="peds-intake-weight" />
        <Tile label="Length / Height" value={length_cm != null ? `${length_cm} cm` : '—'} sub={lPct ? `${lPct.percentile}%ile` : null} accent={toneFor(lPct)} testId="peds-intake-length" />
        {hc_cm != null ? (
          <Tile label="Head circumference" value={`${hc_cm} cm`} sub={hcPct ? `${hcPct.percentile}%ile` : null} accent={toneFor(hcPct)} testId="peds-intake-hc" />
        ) : (
          <Tile label="Head circumference" value="N/A >2y" accent="slate" testId="peds-intake-hc" />
        )}
        <Tile label="BMI-for-age" value={bmi != null ? bmi.toFixed(1) : '—'} sub={bPct ? `${bPct.percentile}%ile` : null} accent={toneFor(bPct)} testId="peds-intake-bmi" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="p-3 rounded-lg bg-white border border-slate-100">
          <div className="flex items-center gap-1.5 mb-1">
            <Activity className="h-3 w-3 text-slate-500" />
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Development</p>
          </div>
          <p className="text-[11px] text-slate-700">{milestones || '—'}</p>
        </div>
        <div className="p-3 rounded-lg bg-white border border-slate-100">
          <div className="flex items-center gap-1.5 mb-1">
            <Syringe className="h-3 w-3 text-slate-500" />
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Vaccines due today</p>
          </div>
          {vaccinesDue?.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {vaccinesDue.map((v) => (
                <span key={v} className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-800">{v}</span>
              ))}
            </div>
          ) : (
            <p className="text-[11px] text-slate-400 italic">None per ACIP schedule</p>
          )}
        </div>
      </div>
    </div>
  );
};

/* ════════════ PSYCHIATRY OVERVIEW ════════════ */
const PsychOverview = ({ intake, onScan }) => {
  const { phq9, gad7, cssrs, siRisk, lastSession, adherence, safetyPlan } = intake;

  const phqTone = phq9 >= 20 ? 'red' : phq9 >= 15 ? 'amber' : phq9 >= 10 ? 'amber' : 'emerald';
  const phqLabel = phq9 >= 20 ? 'Severe' : phq9 >= 15 ? 'Mod-severe' : phq9 >= 10 ? 'Moderate' : phq9 >= 5 ? 'Mild' : 'Minimal';
  const gadTone = gad7 >= 15 ? 'red' : gad7 >= 10 ? 'amber' : 'emerald';
  const gadLabel = gad7 >= 15 ? 'Severe' : gad7 >= 10 ? 'Moderate' : gad7 >= 5 ? 'Mild' : 'Minimal';
  const cssrsTone = cssrs >= 3 ? 'red' : cssrs >= 1 ? 'amber' : 'emerald';
  const siTone = siRisk === 'moderate' || siRisk === 'high' ? 'red' : siRisk === 'passive' || siRisk === 'low' ? 'amber' : 'emerald';

  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50/30 p-4 space-y-3" data-testid="specialty-intake-psych">
      <div className="flex items-center gap-2 flex-wrap">
        <Brain className="h-4 w-4 text-indigo-700" />
        <p className="text-xs font-bold text-slate-900">Behavioral health intake</p>
        <Badge className="text-[9px] bg-white text-slate-500 border border-slate-200">MA-administered</Badge>
        {(siRisk === 'moderate' || siRisk === 'high' || cssrs >= 3) && (
          <Badge className="text-[9px] bg-red-100 text-red-800 border-0 gap-1">
            <ShieldAlert className="h-2.5 w-2.5" /> Risk flag — review first
          </Badge>
        )}
        <div className="ml-auto">
          {onScan && <LensBridgeBtn onClick={() => onScan('psych_screener')} label="Scan screeners" testId="psych-intake-scan-btn" />}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 p-3 rounded-lg bg-white border border-slate-100">
        <Tile label="PHQ-9" value={`${phq9} / 27`} sub={phqLabel} accent={phqTone} testId="psych-intake-phq9" />
        <Tile label="GAD-7" value={`${gad7} / 21`} sub={gadLabel} accent={gadTone} testId="psych-intake-gad7" />
        <Tile label="C-SSRS" value={`${cssrs} / 5`} sub={`SI risk: ${siRisk}`} accent={cssrsTone} testId="psych-intake-cssrs" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[11px]">
        <div className="p-3 rounded-lg bg-white border border-slate-100">
          <p className="text-[9px] uppercase tracking-wide text-slate-400 mb-0.5">Last session</p>
          <p className="font-medium text-slate-800">{lastSession || '—'}</p>
        </div>
        <div className="p-3 rounded-lg bg-white border border-slate-100">
          <p className="text-[9px] uppercase tracking-wide text-slate-400 mb-0.5">Medication adherence</p>
          <p className="font-medium text-slate-800">{adherence || '—'}</p>
        </div>
        <div className={`p-3 rounded-lg bg-white border ${siTone === 'red' ? 'border-red-300 bg-red-50/40' : 'border-slate-100'}`}>
          <p className="text-[9px] uppercase tracking-wide text-slate-400 mb-0.5">Safety plan</p>
          <p className={`font-medium ${siTone === 'red' ? 'text-red-700' : 'text-slate-800'}`}>{safetyPlan || '—'}</p>
        </div>
      </div>
    </div>
  );
};

/* ════════════ OB/GYN OVERVIEW ════════════ */
const ObgynOverview = ({ intake, onScan }) => {
  const {
    gravida, para, lmp, gaWeeks, gaDays, edd, fundalHeight, fht, gbs,
    trimester, deliveryDate, deliveryType, breastfeeding, lastPap, papResult,
    mammo, concern, goal, carrierScreen,
  } = intake;

  const isPrenatal = gaWeeks != null && gaWeeks > 0;
  const isPostpartum = trimester === 'postpartum';

  const fundalTone = isPrenatal && fundalHeight && Math.abs(fundalHeight - gaWeeks) <= 3 ? 'emerald' : isPrenatal && fundalHeight ? 'amber' : 'slate';
  const fhtTone = fht && fht >= 110 && fht <= 160 ? 'emerald' : fht ? 'red' : 'slate';

  return (
    <div className="rounded-xl border border-rose-200 bg-rose-50/30 p-4 space-y-3" data-testid="specialty-intake-obgyn">
      <div className="flex items-center gap-2 flex-wrap">
        <HeartPulse className="h-4 w-4 text-rose-700" />
        <p className="text-xs font-bold text-slate-900">
          {isPostpartum ? 'Postpartum intake' : isPrenatal ? 'Prenatal intake' : 'OB/GYN intake'}
        </p>
        <Badge className="text-[9px] bg-rose-100 text-rose-800 border-0">G{gravida}P{para}{isPrenatal && ` · ${gaWeeks}w${gaDays}d`}{trimester && !isPostpartum ? ` · ${trimester}` : ''}</Badge>
        <Badge className="text-[9px] bg-white text-slate-500 border border-slate-200">MA-entered</Badge>
        <div className="ml-auto">
          {onScan && <LensBridgeBtn onClick={() => onScan('obgyn_labs')} label="Scan prenatal lab" testId="obgyn-intake-scan-btn" />}
        </div>
      </div>

      {isPrenatal && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-3 rounded-lg bg-white border border-slate-100">
            <Tile label="LMP" value={lmp || '—'} testId="obgyn-intake-lmp" />
            <Tile label="GA" value={`${gaWeeks}w ${gaDays}d`} testId="obgyn-intake-ga" />
            <Tile label="EDD" value={edd || '—'} testId="obgyn-intake-edd" />
            <Tile label="Fundal height" value={fundalHeight != null ? `${fundalHeight} cm` : '—'} sub={fundalTone === 'emerald' ? 'WNL' : fundalTone === 'amber' ? 'check growth' : null} accent={fundalTone} testId="obgyn-intake-fundal" />
            <Tile label="FHT" value={fht != null ? `${fht} bpm` : '—'} sub={fhtTone === 'emerald' ? 'reassuring' : fhtTone === 'red' ? 'abnormal' : null} accent={fhtTone} testId="obgyn-intake-fht" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="p-3 rounded-lg bg-white border border-slate-100 text-[11px]">
              <p className="text-[9px] uppercase tracking-wide text-slate-400 mb-0.5">GBS status</p>
              <p className="font-medium text-slate-800">{gbs ? gbs.charAt(0).toUpperCase() + gbs.slice(1) : '—'}</p>
            </div>
            <div className="p-3 rounded-lg bg-white border border-slate-100 text-[11px]">
              <p className="text-[9px] uppercase tracking-wide text-slate-400 mb-0.5">Trimester</p>
              <p className="font-medium text-slate-800">{trimester || '—'}</p>
            </div>
          </div>
        </>
      )}

      {isPostpartum && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 rounded-lg bg-white border border-slate-100">
          <Tile label="Delivery date" value={deliveryDate || '—'} testId="obgyn-intake-delivery" />
          <Tile label="Delivery type" value={deliveryType || '—'} />
          <Tile label="Breastfeeding" value={breastfeeding || '—'} />
          <Tile label="GBS (delivery)" value={gbs || '—'} />
        </div>
      )}

      {!isPrenatal && !isPostpartum && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 rounded-lg bg-white border border-slate-100">
          <Tile label="LMP" value={lmp || '—'} testId="obgyn-intake-lmp" />
          <Tile label="Last Pap" value={lastPap || '—'} sub={papResult || null} />
          {mammo && <Tile label="Mammogram" value={mammo} />}
          {concern && <Tile label="Concern" value={concern} accent="amber" />}
          {goal && <Tile label="Goal" value={goal} accent="indigo" />}
          {carrierScreen && <Tile label="Carrier screen" value={carrierScreen} />}
        </div>
      )}
    </div>
  );
};

/* ════════════ SURGICAL OVERVIEW ════════════ */
const SurgicalOverview = ({ intake, onScan }) => {
  const {
    asa, mets, plannedProcedure, cpt, surgeryDate, pod, globalPeriod, globalPeriodActive,
    anticoag, wound, painScore, labs, consentSigned, consultStage, pt, note,
  } = intake;

  const isPostOp = pod != null;
  const isPreOp = surgeryDate && !isPostOp;
  const metsTone = mets >= 4 ? 'emerald' : 'amber';
  const asaTone = asa === 'I' || asa === 'II' ? 'emerald' : asa === 'III' ? 'amber' : 'red';

  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50/30 p-4 space-y-3" data-testid="specialty-intake-surgical">
      <div className="flex items-center gap-2 flex-wrap">
        <Scissors className="h-4 w-4 text-emerald-700" />
        <p className="text-xs font-bold text-slate-900">
          {isPostOp ? `Post-op · POD ${pod}` : isPreOp ? 'Pre-op intake' : 'Surgical consult'}
        </p>
        <Badge className="text-[9px] bg-emerald-100 text-emerald-800 border-0">{cpt}</Badge>
        {globalPeriodActive && (
          <Badge className="text-[9px] bg-amber-100 text-amber-800 border-0 gap-1">
            <Calendar className="h-2.5 w-2.5" /> Global period active · {globalPeriod}
          </Badge>
        )}
        <Badge className="text-[9px] bg-white text-slate-500 border border-slate-200">MA-entered</Badge>
        <div className="ml-auto">
          {onScan && <LensBridgeBtn onClick={() => onScan('surg_preop')} label={isPostOp ? 'Scan op report' : 'Scan H&P / anesthesia'} testId="surgical-intake-scan-btn" />}
        </div>
      </div>

      <div className="p-3 rounded-lg bg-white border border-slate-100">
        <p className="text-[9px] uppercase tracking-wide text-slate-400">Planned procedure</p>
        <p className="text-[12px] font-semibold text-slate-900">{plannedProcedure} <span className="text-[10px] font-normal text-slate-500">· CPT {cpt}</span></p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 rounded-lg bg-white border border-slate-100">
        <Tile label="ASA class" value={asa} sub={asa === 'I' ? 'Healthy' : asa === 'II' ? 'Mild systemic' : asa === 'III' ? 'Severe systemic' : '—'} accent={asaTone} testId="surgical-intake-asa" />
        <Tile label="METs" value={mets} sub={mets >= 4 ? 'Adequate' : 'Cardiac clearance'} accent={metsTone} testId="surgical-intake-mets" />
        <Tile label="Anticoag" value={anticoag || 'None'} testId="surgical-intake-anticoag" />
        {isPostOp ? (
          <Tile label="Pain" value={painScore != null ? `${painScore} / 10` : '—'} accent={painScore > 5 ? 'red' : 'emerald'} testId="surgical-intake-pain" />
        ) : (
          <Tile label="Surgery date" value={surgeryDate || 'TBD'} testId="surgical-intake-date" />
        )}
      </div>

      {(wound || pt || labs || consultStage || note) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[11px]">
          {wound && (
            <div className="p-3 rounded-lg bg-white border border-slate-100">
              <div className="flex items-center gap-1.5 mb-0.5"><ClipboardCheck className="h-3 w-3 text-slate-500" /><p className="text-[9px] uppercase tracking-wide text-slate-400">Wound status</p></div>
              <p className="font-medium text-slate-800">{wound}</p>
            </div>
          )}
          {pt && (
            <div className="p-3 rounded-lg bg-white border border-slate-100">
              <div className="flex items-center gap-1.5 mb-0.5"><Activity className="h-3 w-3 text-slate-500" /><p className="text-[9px] uppercase tracking-wide text-slate-400">PT progress</p></div>
              <p className="font-medium text-slate-800">{pt}</p>
            </div>
          )}
          {labs && (
            <div className="p-3 rounded-lg bg-white border border-slate-100">
              <div className="flex items-center gap-1.5 mb-0.5"><FileText className="h-3 w-3 text-slate-500" /><p className="text-[9px] uppercase tracking-wide text-slate-400">Pre-op labs</p></div>
              <p className="font-medium text-slate-800">{labs}</p>
            </div>
          )}
          {consultStage && (
            <div className="p-3 rounded-lg bg-white border border-slate-100">
              <div className="flex items-center gap-1.5 mb-0.5"><Stethoscope className="h-3 w-3 text-slate-500" /><p className="text-[9px] uppercase tracking-wide text-slate-400">Consult stage</p></div>
              <p className="font-medium text-slate-800">{consultStage}</p>
            </div>
          )}
          {note && (
            <div className="p-3 rounded-lg bg-white border border-slate-100 md:col-span-2">
              <p className="text-[9px] uppercase tracking-wide text-slate-400 mb-0.5">Pathology / note</p>
              <p className="font-medium text-slate-800">{note}</p>
            </div>
          )}
          {consentSigned === false && isPreOp && (
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-center gap-2 md:col-span-2">
              <AlertTriangle className="h-3 w-3 text-amber-700" />
              <p className="text-[11px] font-medium text-amber-800">Surgical consent NOT yet signed — complete before procedure.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ════════════ ENTRY POINT ════════════ */
export const SpecialtyIntakeCard = ({ patient, specialtyMode, onScanWithLens }) => {
  if (!patient) return null;

  if (specialtyMode === 'soap+peds' && patient.pedsIntake) {
    return <PedsOverview intake={patient.pedsIntake} onScan={onScanWithLens} />;
  }
  if (specialtyMode === 'mental' && patient.psychIntake) {
    return <PsychOverview intake={patient.psychIntake} onScan={onScanWithLens} />;
  }
  if (specialtyMode === 'soap+obgyn' && patient.obgynIntake) {
    return <ObgynOverview intake={patient.obgynIntake} onScan={onScanWithLens} />;
  }
  if (specialtyMode === 'surgical' && patient.surgicalIntake) {
    return <SurgicalOverview intake={patient.surgicalIntake} onScan={onScanWithLens} />;
  }
  return null;
};

export default SpecialtyIntakeCard;
